import * as React from 'react'
import { toast } from 'sonner'

import type { NavGroup, NavItem } from '@/lib/navigation'
import { describeDbError, getSupabase } from '@/lib/supabase'

/**
 * Itens fixados na sidebar: cada pessoa fixa os que mais usa, e eles sobem
 * para o topo, junto do Dashboard (pedido do dono, 29/09).
 *
 * Moram no banco (`member_nav_pin`) para valer em qualquer computador. Uma
 * cópia fica no `localStorage` só para a sidebar já nascer certa, sem piscar,
 * enquanto a leitura do banco não volta.
 */

export const MAX_PINS = 12

/** Os fixados, na ordem em que foram fixados, só entre os itens que a pessoa vê. */
export function pinnedNavItems(groups: readonly NavGroup[], pins: readonly string[]): NavItem[] {
  const visible = new Map<string, NavItem>()
  for (const group of groups) {
    if (group.pinned) continue
    for (const item of group.items) visible.set(item.to, item)
  }
  return pins.flatMap((to) => {
    const item = visible.get(to)
    return item ? [item] : []
  })
}

export function togglePinList(pins: readonly string[], to: string): string[] {
  return pins.includes(to) ? pins.filter((p) => p !== to) : [...pins, to]
}

/** Troca de lugar com o vizinho (-1 sobe, +1 desce). Na ponta, nada muda. */
export function movePinInList(pins: readonly string[], to: string, delta: -1 | 1): string[] {
  const from = pins.indexOf(to)
  const target = from + delta
  if (from < 0 || target < 0 || target >= pins.length) return [...pins]
  const next = [...pins]
  next[from] = pins[target] as string
  next[target] = to
  return next
}

/**
 * Arrastar `from` para o lugar de `to` (o dnd-kit diz sobre quem soltou). Vale
 * sobre a lista inteira do banco, que pode ter rotas que a pessoa não vê mais.
 */
export function reorderPinList(pins: readonly string[], from: string, to: string): string[] {
  const a = pins.indexOf(from)
  const b = pins.indexOf(to)
  if (a < 0 || b < 0 || a === b) return [...pins]
  const next = [...pins]
  next.splice(a, 1)
  next.splice(b, 0, from)
  return next
}

const cacheKey = (memberId: string) => `sistemas:sidebar:fixados:${memberId}`

function readCache(memberId: string | undefined): string[] {
  if (!memberId || typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(cacheKey(memberId))
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : []
  } catch {
    return []
  }
}

function writeCache(memberId: string, pins: readonly string[]) {
  try {
    window.localStorage.setItem(cacheKey(memberId), JSON.stringify(pins))
  } catch {
    // Sem storage: a cópia local só serve para não piscar; o banco segue valendo.
  }
}

export interface NavPins {
  readonly pins: readonly string[]
  readonly toggle: (to: string) => void
  /** Teclado (Alt+↑/↓): troca com o vizinho. */
  readonly move: (to: string, delta: -1 | 1) => void
  /** Arrasto: solta `from` no lugar de `to`. */
  readonly reorder: (from: string, to: string) => void
}

export function useNavPins(memberId: string | undefined): NavPins {
  const [pins, setPins] = React.useState<string[]>(() => readCache(memberId))
  const pinsRef = React.useRef(pins)
  pinsRef.current = pins

  React.useEffect(() => {
    if (!memberId) return
    let alive = true
    setPins(readCache(memberId))
    void getSupabase()
      .from('member_nav_pin')
      .select('item, position')
      .eq('member_id', memberId)
      .order('position')
      .then(({ data, error }) => {
        if (!alive || error) return
        const next = ((data ?? []) as { item: string }[]).map((r) => r.item)
        setPins(next)
        writeCache(memberId, next)
      })
    return () => {
      alive = false
    }
  }, [memberId])

  // Grava a lista inteira na ordem: são no máximo 12 linhas, e assim fixar,
  // soltar e reordenar são o mesmo caminho.
  const save = React.useCallback(
    async (next: string[], previous: string[]) => {
      if (!memberId) return
      setPins(next)
      writeCache(memberId, next)
      const sb = getSupabase()
      const removed = previous.filter((p) => !next.includes(p))
      const results = await Promise.all([
        removed.length
          ? sb.from('member_nav_pin').delete().eq('member_id', memberId).in('item', removed)
          : Promise.resolve({ error: null }),
        next.length
          ? sb
              .from('member_nav_pin')
              .upsert(
                next.map((item, position) => ({ member_id: memberId, item, position })),
                { onConflict: 'member_id,item' },
              )
          : Promise.resolve({ error: null }),
      ])
      const failed = results.find((r) => r.error)?.error
      if (failed) {
        setPins(previous)
        writeCache(memberId, previous)
        toast.error(describeDbError(failed, 'Não foi possível salvar os itens fixados.'))
      }
    },
    [memberId],
  )

  const toggle = React.useCallback(
    (to: string) => {
      const previous = pinsRef.current
      if (!previous.includes(to) && previous.length >= MAX_PINS) {
        toast.info(`Dá para fixar até ${MAX_PINS} itens. Solte um antes.`)
        return
      }
      void save(togglePinList(previous, to), previous)
    },
    [save],
  )

  const move = React.useCallback(
    (to: string, delta: -1 | 1) => {
      const previous = pinsRef.current
      void save(movePinInList(previous, to, delta), previous)
    },
    [save],
  )

  const reorder = React.useCallback(
    (from: string, to: string) => {
      const previous = pinsRef.current
      const next = reorderPinList(previous, from, to)
      if (next.every((p, i) => p === previous[i])) return
      void save(next, previous)
    },
    [save],
  )

  return { pins, toggle, move, reorder }
}
